import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ICarDetails } from '@impact/data';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { clockIcon } from '../../icons';
import { GlobalDataService } from '../core/global-data.service';
import { SidePanelService } from '../side-panel/side-panel.service';
import { UsedCarService } from './used-car.service';
import { SettingsService, SiteIds } from '../core/settings.service';

@Component({
    selector: 'app-used-car-main-details',
    template: `
        <section class="used-car-main-details" *ngIf="car$ | async as car">
            
            <!-- Motorpoint -->
            <div class="used-car-details__intro" *ngIf="site === siteIds.MOTORPOINT">
                <h2 class="used-car-details-primary-heading">{{ car?.make?.value }} {{ car?.model?.value }}</h2>
                <h3 class="used-car-main-details__variant">{{ car?.variant?.value }}</h3>
                <hr class="used-car-details-break" />
                <div class="used-car-main-details__description-container" *ngIf="car?.comment">
                    <p [innerHTML]="car?.comment?.value" class="used-car-main-details__description" [class.used-car-main-details__description--no-clamp]="!descriptionClamp"></p>
                    <button class="used-car-main-details__read-more button--as-link" type="button" (click)="onDescriptionClick()">
                        {{ (descriptionClamp ? 'buttons.read_more_text' : 'buttons.read_less_text') | translate }}
                    </button>
                </div>
                <button type="button" class="used-car-main-details-test-drive-button button--primary" (click)="openTestDriveSidePanel()">
                    <span aria-hidden="true" class="icon used-car-main-details-test-drive-button__icon">${clockIcon}</span>
                    <span class="used-car-main-details-test-drive-button__text"> {{ 'used_car_details.book_test_drive' | translate }}</span>
                </button>
            </div>

            <ul class="used-car-main-details-list">
                <li class="used-car-main-details-list-item" *ngIf="car?.acceleration0To100">
                    <div class="used-car-main-details-list-item__label">{{ car?.acceleration0To100?.label }}</div>
                    <div class="used-car-main-details-list-item__value">{{ car?.acceleration0To100?.value }}</div>
                </li>
                <li class="used-car-main-details-list-item" *ngIf="car?.topSpeed">
                    <div class="used-car-main-details-list-item__label">{{ car?.topSpeed?.label }}</div>
                    <div class="used-car-main-details-list-item__value">{{ car?.topSpeed?.value }}</div>
                </li>
                <li class="used-car-main-details-list-item" *ngIf="car?.kmPerLiter">
                    <div class="used-car-main-details-list-item__label">{{ car?.kmPerLiter?.label }}</div>
                    <div class="used-car-main-details-list-item__value">{{ car?.kmPerLiter?.value }}</div>
                </li>
                <li class="used-car-main-details-list-item" *ngIf="car?.propellant">
                    <div class="used-car-main-details-list-item__label">{{car?.propellant?.label}}</div>
                    <div class="used-car-main-details-list-item__value">{{car?.propellant?.data}}</div>
                </li>
                <li class="used-car-main-details-list-item" *ngIf="car?.gearType">
                    <div class="used-car-main-details-list-item__label">{{ car?.gearType?.label }}</div>
                    <div class="used-car-main-details-list-item__value">{{ car?.gearType?.value }}</div>
                </li>
                <li class="used-car-main-details-list-item" *ngIf="car?.color">
                    <div class="used-car-main-details-list-item__label">{{ car?.color?.label }}</div>
                    <div class="used-car-main-details-list-item__value">{{ car?.color?.value }}</div>
                </li>
            </ul>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarMainDetailsComponent implements OnDestroy {
    car$: Observable<ICarDetails>;
    dealerPageUrl$: Observable<string | undefined>;
    descriptionClamp = true;
    site: string;
    siteIds = SiteIds;
    private readonly unsubscribe = new Subject<void>();

    constructor(
        private sidePanelService: SidePanelService,
        private settingsService: SettingsService,
        globalDataService: GlobalDataService,
        usedCarService: UsedCarService,
    ) {
        this.car$ = usedCarService.carDetails$;

        this.settingsService
            .getSiteId()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((siteId) => {
                this.site = siteId;
            });

        
        // TODO: Fix this, because we do not have pageLink anymore. Look into how the dealerships overview page does it
        // this.dealerPageUrl$ = combineLatest([this.car$, globalDataService.getDealerships()])
        //     .pipe(map(([car, dealerships]) => {
        //         return dealerships.children?.find(dealer => dealer.dealershipID === car.dealer?.data?.dealerId?.toString())?.pageLink?.url
        //     }));
    }

    onDescriptionClick() {
        this.descriptionClamp = !this.descriptionClamp;
    }

    openTestDriveSidePanel() {
        this.sidePanelService.showBookUsedCarTestDriveSidePanel(this.car$);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}